import api from './api';

export type TipoMovimentoFinanceiro = 'EMPENHO' | 'LIQUIDACAO' | 'PAGAMENTO';
export type NaturezaMovimentoFinanceiro = 'ORIGINAL' | 'ANULACAO';

export type OrigemMovimentoFinanceiro = 'MANUAL' | 'COPLAN';

export type ExecucaoFinanceiraMovimento = {
  id: string;
  tipo: TipoMovimentoFinanceiro;
  natureza: NaturezaMovimentoFinanceiro;
  movimentoOrigemId?: string;
  numeroDocumento: string;
  dataMovimento: string;
  valor: number;
  credor?: string;
  observacao?: string;
  justificativa?: string;
  documentoReferencia?: string;
  usuarioRegistro: string;
  dataRegistro: string;
  saldoDisponivel: number;
  saldoAnulavel: number;
  origem?: OrigemMovimentoFinanceiro;
  substituidoPorCoplan?: boolean;
  /** Contrato do Coplan ao qual este empenho está vinculado (só empenhos importados). */
  contratoId?: string | null;
};

export type InstrumentoContrato = 'TERMO_FOMENTO' | 'CONTRATO';

export type ExecucaoContrato = {
  id: string;
  instrumento: InstrumentoContrato;
  tipoCodigo?: string;
  numero?: string;
  ano?: number;
  vigenciaInicio?: string;
  vigenciaFim?: string;
  assinatura?: string;
  objeto?: string;
  valor?: number;
  /** Quanto desta emenda foi empenhado neste contrato (um contrato pode atender várias emendas). */
  empenhadoLiquido: number;
  /** Processo de compra (licitação/dispensa/inexigibilidade) que originou o contrato. */
  processoCompraNumero?: string;
  processoCompraAno?: number;
  processoCompraData?: string;
};

export type AtoLegalEmenda = {
  id: string;
  natureza: string;
  tipoCodigo?: string;
  numero?: string;
  ano?: number;
  dataPublicacao?: string;
  dataRevogacao?: string;
  ementa?: string;
  status?: string;
  linkOficial?: string;
};

export type ExecucaoFinanceiraResumo = {
  emendaId: string;
  instituicaoNome?: string;
  planoTrabalhoId?: string;
  planoStatus?: string;
  liberada: boolean;
  motivoBloqueio?: string;
  documentoLiberacaoAprovado: boolean;
  motivoBloqueioLiquidacao?: string;
  valorOrcado: number;
  empenhadoBruto: number;
  empenhoAnulado: number;
  empenhadoLiquido: number;
  liquidadoBruto: number;
  liquidacaoAnulada: number;
  liquidadoLiquido: number;
  pagoBruto: number;
  pagamentoEstornado: number;
  pagoLiquido: number;
  saldoEmpenhar: number;
  saldoLiquidar: number;
  saldoPagar: number;
  situacao: string;
  movimentos: ExecucaoFinanceiraMovimento[];
  contratos: ExecucaoContrato[];
  atosLegais: AtoLegalEmenda[];
};

export type MovimentoFinanceiroPayload = {
  tipo: TipoMovimentoFinanceiro;
  movimentoOrigemId?: string;
  numeroDocumento: string;
  dataMovimento: string;
  valor: number;
  observacao?: string;
  documentoReferencia?: string;
};

export type AnulacaoFinanceiraPayload = {
  numeroDocumento: string;
  dataMovimento: string;
  valor: number;
  justificativa: string;
  documentoReferencia?: string;
};

export const numeroContratoComAno = (numero?: string, ano?: number) => {
  if (!numero) return '—';
  return ano ? `${numero}/${ano}` : numero;
};

export const rotuloContrato = (contrato: Pick<ExecucaoContrato, 'instrumento' | 'numero' | 'ano'>) =>
  `${contrato.instrumento === 'TERMO_FOMENTO' ? 'Termo de Fomento' : 'Contrato'} ${numeroContratoComAno(contrato.numero, contrato.ano)}`;

const normalize = (data: unknown): ExecucaoFinanceiraResumo => {
  const raw = data as ExecucaoFinanceiraResumo;
  return ({
  ...raw,
  documentoLiberacaoAprovado: Boolean(raw?.documentoLiberacaoAprovado),
  valorOrcado: Number(raw?.valorOrcado ?? 0),
  empenhadoBruto: Number(raw?.empenhadoBruto ?? 0),
  empenhoAnulado: Number(raw?.empenhoAnulado ?? 0),
  empenhadoLiquido: Number(raw?.empenhadoLiquido ?? 0),
  liquidadoBruto: Number(raw?.liquidadoBruto ?? 0),
  liquidacaoAnulada: Number(raw?.liquidacaoAnulada ?? 0),
  liquidadoLiquido: Number(raw?.liquidadoLiquido ?? 0),
  pagoBruto: Number(raw?.pagoBruto ?? 0),
  pagamentoEstornado: Number(raw?.pagamentoEstornado ?? 0),
  pagoLiquido: Number(raw?.pagoLiquido ?? 0),
  saldoEmpenhar: Number(raw?.saldoEmpenhar ?? 0),
  saldoLiquidar: Number(raw?.saldoLiquidar ?? 0),
  saldoPagar: Number(raw?.saldoPagar ?? 0),
  movimentos: Array.isArray(raw?.movimentos)
    ? raw.movimentos.map((item) => ({
        ...item,
        valor: Number(item?.valor ?? 0),
        saldoDisponivel: Number(item?.saldoDisponivel ?? 0),
        saldoAnulavel: Number(item?.saldoAnulavel ?? 0),
      }))
    : [],
  contratos: Array.isArray(raw?.contratos)
    ? raw.contratos.map((item) => ({
        ...item,
        valor: item?.valor == null ? undefined : Number(item.valor),
        empenhadoLiquido: Number(item?.empenhadoLiquido ?? 0),
      }))
    : [],
  atosLegais: Array.isArray(raw?.atosLegais) ? raw.atosLegais : [],
  });
};

const getResumo = async (emendaId: string): Promise<ExecucaoFinanceiraResumo> => {
  const response = await api.get(`/emendas/${emendaId}/execucao-financeira`);
  return normalize(response.data);
};

const registrar = async (emendaId: string, payload: MovimentoFinanceiroPayload): Promise<ExecucaoFinanceiraResumo> => {
  const response = await api.post(`/emendas/${emendaId}/execucao-financeira/movimentos`, payload);
  return normalize(response.data);
};

const anular = async (emendaId: string, movimentoId: string, payload: AnulacaoFinanceiraPayload): Promise<ExecucaoFinanceiraResumo> => {
  const response = await api.post(`/emendas/${emendaId}/execucao-financeira/movimentos/${movimentoId}/anulacoes`, payload);
  return normalize(response.data);
};

export type CoplanSyncResumo = {
  created: number;
  updated: number;
  skipped: number;
  contratosCriados?: number;
  contratosAtualizados?: number;
  atosLegaisCriados?: number;
  atosLegaisAtualizados?: number;
  vinculosAtoLegalCriados?: number;
  warnings: string[];
};

const sincronizarComCoplan = async (forceReimport = false): Promise<CoplanSyncResumo> => {
  const response = await api.post('/admin/coplan/execucao-financeira/sync', null, {
    params: { forceReimport },
  });
  return response.data as CoplanSyncResumo;
};

export default { getResumo, registrar, anular, sincronizarComCoplan };
